import type { CSSProperties, ComponentPropsWithRef, FC } from 'react';

import type { ClassNameProps } from '@tripleten/cn';

import { Slot } from '../slot/slot.tsx';

import { cnBox } from './box.constants.ts';

import './box.css';

import './_m/box_m.css';
import './_mb/box_mb.css';
import './_ml/box_ml.css';
import './_mr/box_mr.css';
import './_mt/box_mt.css';
import './_mx/box_mx.css';
import './_my/box_my.css';
import './_p/box_p.css';
import './_pb/box_pb.css';
import './_pl/box_pl.css';
import './_pr/box_pr.css';
import './_pt/box_pt.css';
import './_px/box_px.css';
import './_py/box_py.css';

type SpaceScale = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type NegativeScale = 'n1' | 'n2' | 'n3' | 'n4' | 'n5' | 'n6' | 'n7' | 'n8' | 'n9';
type MarginScale = SpaceScale | NegativeScale;

/**
 * A generic layout container. Renders a `div` by default with support for
 * margin and padding props.
 *
 * Basic usage:
 * ```tsx
 * <Box mb="4">Content</Box>
 * ```
 *
 * With padding:
 * ```tsx
 * <Box px="4" py="6">Content</Box>
 * ```
 *
 * As a span:
 * ```tsx
 * <Box as="span" ml="2">Inline content</Box>
 * ```
 */
export type BoxProps = ClassNameProps &
    ComponentPropsWithRef<'div'> & {
        /**
         * Change the default rendered element for the one passed as a child, merging their props and behavior.
         */
        asChild?: boolean;
        /**
         * The HTML element to render. Does not affect visual appearance.
         */
        as?: 'div' | 'span';
        /**
         * Sets margin on all sides.
         */
        m?: MarginScale;
        /**
         * Sets margin on the left and right sides.
         */
        mx?: MarginScale;
        /**
         * Sets margin on the top and bottom sides.
         */
        my?: MarginScale;
        /**
         * Sets margin on the top side.
         */
        mt?: MarginScale;
        /**
         * Sets margin on the right side.
         */
        mr?: MarginScale;
        /**
         * Sets margin on the bottom side.
         */
        mb?: MarginScale;
        /**
         * Sets margin on the left side.
         */
        ml?: MarginScale;
        /**
         * Sets padding on all sides.
         */
        p?: SpaceScale;
        /**
         * Sets padding on the left and right sides.
         */
        px?: SpaceScale;
        /**
         * Sets padding on the top and bottom sides.
         */
        py?: SpaceScale;
        /**
         * Sets padding on the top side.
         */
        pt?: SpaceScale;
        /**
         * Sets padding on the right side.
         */
        pr?: SpaceScale;
        /**
         * Sets padding on the bottom side.
         */
        pb?: SpaceScale;
        /**
         * Sets padding on the left side.
         */
        pl?: SpaceScale;
    } & Pick<
        CSSProperties,
        | 'width'
        | 'minWidth'
        | 'maxWidth'
        | 'height'
        | 'minHeight'
        | 'maxHeight'
        | 'flexShrink'
        | 'flexGrow'
        | 'flexBasis'
        | 'alignSelf'
        | 'justifySelf'
    >;

export const Box: FC<BoxProps> = ({
    as = 'div',
    asChild,
    className,
    style,
    m,
    mx,
    my,
    mt,
    mr,
    mb,
    ml,
    p,
    px,
    py,
    pt,
    pr,
    pb,
    pl,
    width,
    minWidth,
    maxWidth,
    height,
    minHeight,
    maxHeight,
    flexShrink,
    flexGrow,
    flexBasis,
    alignSelf,
    justifySelf,
    ...props
}) => {
    const Component = asChild ? Slot : as;

    return (
        <Component
            {...props}
            style={{
                width,
                minWidth,
                maxWidth,
                height,
                minHeight,
                maxHeight,
                flexShrink,
                flexGrow,
                flexBasis,
                alignSelf,
                justifySelf,
                ...style,
            }}
            className={cnBox({ m, mx, my, mt, mr, mb, ml, p, px, py, pt, pr, pb, pl }, [className])}
        />
    );
};
